// visual_verdict — la retouche de l'opérateur a-t-elle cassé la mise en page ?
//
// ── CE QUE ÇA TIENT ─────────────────────────────────────────────────────────
//
// Sa contrainte, le 2026-10-05 : « on veut aussi que ca ne nique pas la responsivité ». Les
// trois gestes ne produisent que des pourcentages et un ratio — jamais un pixel — donc une
// retouche ne peut pas figer une largeur. Mais un pourcentage peut quand même faire déborder
// une page : une photo à 100 % dans un conteneur qui a déjà des marges sort de l'écran.
//
// La promesse ne vaut donc que mesurée. On rend l'app à plusieurs largeurs AVANT et APRÈS, et
// on refuse la retouche qui a aggravé les choses.
//
// ── POURQUOI AVANT *ET* APRÈS ───────────────────────────────────────────────
//
// C'est la décision de produit de ce module, et elle n'est pas évidente : beaucoup d'apps
// débordent déjà quelque part. Juger l'app APRÈS seulement refuserait toute retouche sur une
// app imparfaite — précisément celles qu'un opérateur veut retoucher. Et il ne pourrait rien y
// faire : le défaut ne vient pas de son geste, et son geste n'a aucun moyen de le réparer.
//
// Alors on ne juge que la DIFFÉRENCE. Un débordement préexistant traverse ; un débordement que
// la retouche ajoute ou creuse est refusé.
//
// ── ET LES RÈGLES NE SONT PAS ICI ───────────────────────────────────────────
//
// « Est-ce que ça déborde ? » est déjà écrit, éprouvé et muté dans ./viewport_audit — celui que
// `bun run audit:viewports` emploie sur 84 combinaisons. On l'importe au lieu de le recopier :
// deux copies d'une même règle finissent par divergence, et une divergence ici dirait « cassé »
// d'un côté et « sain » de l'autre, ce que lib/browser_key_store.ts refuse déjà pour la même
// raison.
//
// On n'en prend qu'une partie, volontairement : `smallTargetFindings` n'est PAS employée.
// Déplacer ou redimensionner une photo ne peut pas rendre un bouton trop petit, et juger ce
// qu'un geste ne peut pas changer ferait refuser une retouche pour un défaut sans rapport.
// L'audit complet, lui, les juge déjà.
//
// ── POURQUOI CE MODULE VIT DANS LA CONSOLE ──────────────────────────────────
//
// Parce que c'est là que la mesure est gratuite. La console rend déjà l'app dans une iframe aux
// trois largeurs de PREVIEW_WIDTHS, et le runtime porte déjà l'aperçu en direct du geste : on
// peut donc mesurer PENDANT le glissement, dans le navigateur de l'opérateur.
//
// L'autre dessin — mesurer sur le nœud — demandait une sonde Playwright de plus, un point
// d'entrée sur le déployeur, et UN DÉPLOIEMENT PAR RETOUCHE pour une fonction faite de « petits
// détails ». Et il arrivait trop tard : il refuse après coup ce que celui-ci montre pendant.
//
// Ce que ça n'est donc pas : une frontière de sûreté. Un POST fabriqué à la main peut encore
// enregistrer une valeur qui casse — mais elle ne casse que la mise en page de l'app de celui
// qui l'a envoyée, et c'est son app. Si un jour ça doit être tenu côté serveur, ce module est
// pur : il s'importe tel quel, sans rien y changer.
import type { MeasuredPage } from "../../../frontend-unit-bundle/platform/src/_runtime/visual_bridge";
import {
  type Box,
  OVERFLOW_TOLERANCE_PX,
  overflowFindings,
  pageScrollsSideways,
} from "./viewport_audit";

/** Ce que le navigateur rapporte à UNE largeur. Des nombres bruts, pas un jugement : la règle
 *  vit à un seul endroit, et c'est ici qu'on l'applique. */
export type WidthMeasure = {
  /** La largeur de fenêtre DEMANDÉE pour ce rendu. C'est la clé qui apparie avant et après, et
   *  le nombre qu'on montre à l'opérateur — ce n'est PAS celui contre lequel on juge. */
  readonly width: number;
  readonly scrollWidth: number;
  /** La largeur VISIBLE, barre de défilement exclue. C'est la référence du débordement. */
  readonly clientWidth: number;
  readonly boxes: readonly Box[];
};

export type Verdict = { readonly ok: true; readonly why?: undefined } | { readonly ok: false; readonly why: string };

/**
 * CE QUI ARRIVE PAR LE FIL DOIT ÊTRE JUGEABLE TEL QUEL.
 *
 * L'app se mesure elle-même — la console ne peut pas lire le DOM d'une autre origine — et
 * renvoie un `MeasuredPage` par visual_bridge. Si cette forme et `WidthMeasure` divergent, la
 * garde s'éteint EN SILENCE : plus rien n'est mesuré, et aucun test de comportement ne le dit.
 *
 * L'invariant est donc posé ici, dans un fichier que `tsc -b` bâtit. Il ne pouvait PAS vivre
 * dans le fichier de test : ce paquet exclut les tests du build (tsconfig.json, raison trouvée
 * le 2026-09-25), et un mutant qui élargissait `MeasuredPage.width` en `number | null` y
 * survivait avec zéro erreur.
 *
 * Purement des types : `import type` est effacé à la compilation, donc rien du runtime de l'app
 * n'entre dans le paquet de la console.
 */
type Assignable<T extends true> = T;
type _AccordDuFil = Assignable<MeasuredPage extends WidthMeasure ? true : false>;

const OK: Verdict = { ok: true };

/** Le pire débordement par étiquette.
 *
 *  Deux boîtes peuvent porter la même étiquette — une liste rendue en boucle — et c'est un
 *  maximum qu'il faut comparer à un maximum, sinon l'ordre du DOM déciderait du verdict.
 *
 *  Écrit avec `Math.max` et non avec un `if` : `overflowFindings` rend sa liste déjà triée, donc
 *  une comparaison serait vraie à chaque fois et personne ne pourrait prouver qu'elle sert. Une
 *  expression totale dit la même chose sans créer de branche invérifiable.
 *
 *  ON JUGE CONTRE `clientWidth`, PAS CONTRE `width`. La largeur demandée inclut la barre de
 *  défilement verticale ; la zone réellement visible est `documentElement.clientWidth`. Juger
 *  contre la demandée laisserait passer une quinzaine de pixels qui sortent bel et bien de
 *  l'écran. C'est aussi l'appel exact que `scripts/viewport_audit.ts` fait déjà (`overflowFindings
 *  (m.boites, m.clientWidth)`) : importer la règle ne suffisait pas, l'APPEL pouvait diverger. */
function pireParEtiquette(m: WidthMeasure): Map<string, number> {
  const out = new Map<string, number>();
  for (const f of overflowFindings(m.boxes, m.clientWidth)) {
    out.set(f.label, Math.max(f.overflowPx, out.get(f.label) ?? Number.NEGATIVE_INFINITY));
  }
  return out;
}

/**
 * L'aggravation la plus grave à cette largeur, ou `null` si la retouche n'a rien empiré.
 *
 * ON CLASSE PAR CE QUE LA RETOUCHE A AJOUTÉ, pas par la taille du débordement. La différence
 * compte : une app peut déjà avoir une section qui sort de 500 px. Si la retouche l'aggrave de
 * 20 px et casse en même temps une photo de 300 px, c'est la PHOTO qu'il faut nommer — c'est
 * elle que le geste a abîmée, et c'est la seule que l'opérateur peut réparer. Nommer le plus
 * gros débordement l'enverrait vers un défaut qui ne lui appartient pas.
 *
 * Un élément absent d'avant comptait pour zéro : c'est exactement ce qu'il débordait.
 */
function aggravation(
  avant: WidthMeasure,
  apres: WidthMeasure,
): { label: string; px: number } | null {
  const refs = pireParEtiquette(avant);
  let pire: { label: string; px: number; creuse: number } | null = null;
  for (const [label, px] of pireParEtiquette(apres)) {
    const creuse = px - (refs.get(label) ?? 0);
    // La même tolérance que l'audit, importée : le navigateur rend des demi-pixels, et sans
    // elle le bruit d'arrondi ferait refuser des retouches parfaitement saines.
    if (creuse <= OVERFLOW_TOLERANCE_PX) continue;
    if (pire === null || creuse > pire.creuse) pire = { label, px, creuse };
  }
  // On RAPPORTE le débordement total et non l'aggravation : c'est ce que l'opérateur voit à
  // l'écran, donc le seul des deux nombres qu'il peut vérifier de ses yeux.
  return pire === null ? null : { label: pire.label, px: pire.px };
}

/**
 * PURE. La retouche est-elle acceptable, au vu des mesures d'avant et d'après ?
 *
 * ON NE REFUSE JAMAIS SUR UNE ABSENCE DE PREUVE. Pas de mesure, une largeur d'après sans
 * équivalent d'avant, un chiffre illisible : tout cela vaut « acceptable ». Le navigateur qui
 * n'a pas répondu est notre problème, pas celui de l'opérateur, et lui refuser son geste pour
 * ça le punirait d'une panne qui n'est pas la sienne.
 *
 * Les largeurs sont parcourues de la plus étroite à la plus large, indépendamment de l'ordre
 * reçu : le verdict ne doit pas dépendre de l'ordre d'un tableau, et c'est sur un téléphone
 * qu'une photo trop large se voit d'abord.
 */
export function verdictFor(
  avant: readonly WidthMeasure[],
  apres: readonly WidthMeasure[],
): Verdict {
  const refs = new Map(avant.map((m) => [m.width, m]));
  const ordonnees = [...apres].sort((a, z) => a.width - z.width);

  for (const a of ordonnees) {
    const ref = refs.get(a.width);
    if (ref === undefined) continue;

    const coupable = aggravation(ref, a);
    if (coupable !== null) {
      return {
        ok: false,
        why: `À ${a.width} px de large, la retouche fait sortir « ${coupable.label} » de l'écran de ${coupable.px} px. Elle n'est pas enregistrée.`,
      };
    }

    // LE DÉFILEMENT LATÉRAL SANS COUPABLE NOMMÉ. Il arrive : ce qui déborde peut être un
    // pseudo-élément ou une boîte que la mesure n'a pas relevée. On refuse quand même — la
    // page défile de côté et elle ne le faisait pas avant — mais on ne nomme rien, parce
    // qu'inventer un coupable enverrait réparer le mauvais endroit.
    if (pageScrollsSideways(a.scrollWidth, a.clientWidth) && !pageScrollsSideways(ref.scrollWidth, ref.clientWidth)) {
      return {
        ok: false,
        why: `À ${a.width} px de large, la retouche fait défiler la page latéralement. Elle n'est pas enregistrée.`,
      };
    }
  }

  return OK;
}
